import {
  useToast,
  useDisclosure,
  Box,
  Flex,
  Divider,
  Text,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Tooltip,
  IconButton,
} from '@chakra-ui/react';
import {
  CompositionGroup,
  compositionGroupApi,
  CopyConfig,
} from '@texas/api/endpoints/compositionGroup/compositionGroupApi';
import { FileLink } from '@texas/api/endpoints/filesApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useFileUploads } from '@texas/api/hooks/useFileUploads';
import { compositionGroupEvents } from '@texas/components/shared/compositionGroup/events';
import { RenameGroupModal } from '@texas/components/shared/compositionGroup/RenameGroupModal';
import { VerifyDialogWithFunction } from '@texas/components/shared/dialog/VerifyDialogWithFunction';
import { VerifyDialogWithRequest } from '@texas/components/shared/dialog/VerifyDialogWithRequest';
import { ErrorsList } from '@texas/components/shared/ErrorsList';
import { FileBrowserModal } from '@texas/components/shared/files/FileBrowserModal';
import { defaultIconSize, Icons } from '@texas/components/shared/Icons';
import { WarningComponent } from '@texas/components/shared/WarningComponent';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { ServerError } from '@texas/types';
import { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CompositionListModal } from '../../compositions/CompositionListModal';
import { DocumentFileModal } from '../../compositions/shared/file/DocumentFileModal';
import { ConfirmCopy } from '../../compositions/shared/group/ConfirmCopy';
import { MatrixColors } from '../matrix/matrixColors/MatrixColors';
import { MatrixSizes } from '../matrix/matrixSizes/MatrixSizes';
import { MarkReady } from './MarkReady';
import { OptionsComponent } from './OptionsComponent';
import { request } from '@texas/utils/helpers/httpHelpers';
import { CompositionGroupTitle } from '@texas/components/shared/compositionGroup/CompositionGroupTitle';
import { TFunction } from 'i18next';
import { imageExtensions } from '@texas/utils/helpers/filesHelper';
import { LayoutDropzone } from './LayoutDropZone';
import { CompositionGroupHistoryModal } from '../../history/CompositionGroupHistoryModal';
import { ArtworkDropzone } from './ArtworkDropzone';

import { reactEvents } from '@bridge/reactEvents';

export function CompositionGroupContainer({
  option,
  isMatrix,
  isVdc,
  onArchive,
  onCopy,
}: {
  option: CompositionGroup;
  isMatrix: boolean;
  isVdc: boolean;
  onArchive?: () => Promise<void>;
  onCopy?: (fromId: number, copyConfig?: CopyConfig) => Promise<void>;
}) {
  const toast = useToast();
  const { t } = useTranslation();
  const [sizesWarning, setSizesWarning] = useState<string | undefined>();
  const [colorsWarning, setColorsWarning] = useState<string | undefined>();

  const {
    isOpen: isArchivedCompositionsOpen,
    onClose: onArchivedCompositionsClose,
    onOpen: onArchivedCompositionsOpen,
  } = useDisclosure();

  const {
    isOpen: isFileBrowserOpen,
    onClose: onFileBrowserClose,
    onOpen: onFileBrowserOpen,
  } = useDisclosure();

  const {
    isOpen: isFileModalOpen,
    onClose: onFileModalClose,
    onOpen: onfileModalOpen,
  } = useDisclosure();

  const {
    isOpen: isRemoveFileOpen,
    onClose: onRemoveFileClose,
    onOpen: onRemoveFileOpen,
  } = useDisclosure();
  const { request: updateRequest } = useApiRequest(
    compositionGroupApi.updateFile,
  );

  const updateFile = useCallback(
    (file: FileLink) => {
      request(
        updateRequest,
        [option.id, file.id],
        (_) => {
          toast({
            title: t('fileBrowser.updatedFile'),
            status: 'success',
            isClosable: true,
          });
          compositionGroupEvents.onLayoutChanged.dispatch();
        },
        (error: ServerError) => {
          toast({
            title: t('fileBrowser.updateFileFailed'),
            description: <ErrorsList errors={error.errors} />,
            status: 'error',
            isClosable: true,
          });
        },
      );
    },
    [option.id, t, toast, updateRequest],
  );

  const { request: markNotReadyReqest, loading: notReadyLoading } =
    useApiRequest(compositionGroupApi.markNotReady);
  const { request: markReadyRequest, loading: readyLoading } = useApiRequest(
    compositionGroupApi.markReady,
  );

  const markNotReady = async () =>
    await request(
      markNotReadyReqest,
      [option.id],
      () => {
        toast({
          title: t('compositionGroup.option.markedNotReady'),
          status: 'success',
          isClosable: true,
        });
        compositionGroupEvents.onMarkNotReady.dispatch();
      },
      (error: ServerError) => {
        toast({
          title: t('general.actionFailed'),
          description: error.message,
          status: 'error',
          isClosable: true,
        });
      },
    );

  const markReady = async () =>
    await request(
      markReadyRequest,
      [option.id],
      () => {
        toast({
          title: t('compositionGroup.option.markedReady'),
          status: 'success',
          isClosable: true,
        });
        compositionGroupEvents.onMarkReady.dispatch();
      },
      (error: ServerError) => {
        toast({
          title: t('general.actionFailed'),
          description: error.message,
          status: 'error',
          isClosable: true,
        });
      },
    );

  const {
    fileUploads,
    isUploading,
    uploadFailed,
    uploadFiles,
    removeFileUpload,
  } = useFileUploads(updateFile);

  const {
    isOpen: isEditGroupOpen,
    onClose: onEditGroupClose,
    onOpen: onEditGroupOpen,
    value: editGroupValue,
  } = useValueDisclosure<{ id: number; note: string | null }>();
  const {
    isOpen: isArchiveOpen,
    onClose: onArchiveClose,
    onOpen: onArchiveOpen,
  } = useDisclosure();
  const {
    isOpen: isCopyOpen,
    onClose: onCopyClose,
    onOpen: onCopyOpen,
    value: copyValue,
  } = useValueDisclosure<{
    version: number;
    id: number;
  }>();
  const {
    isOpen: isHistoryOpen,
    onClose: onHistoryClose,
    onOpen: onHistoryOpen,
    value: historyValue,
  } = useValueDisclosure<{
    id: number;
    name: string;
  }>();

  const ref = useRef<HTMLDivElement>(null);

  return (
    <Box w="full" overflow="hidden" ref={ref}>
      <Flex role="group" flexDir="column">
        <CompositionGroupTitle
          version={option.version}
          note={option.note}
          type="option"
          linkedVariantId={option.linkedVariantId}
        />
        <Flex gap={4} flexWrap="wrap">
          <Flex gap={4}>
            <LayoutDropzone
              onUpload={(files) => uploadFiles(files, option.folderId)}
              onfileModalOpen={onfileModalOpen}
              fileId={option.fileId}
            />
            <ArtworkDropzone
              compositionGroupId={option.id}
              fileId={option.artworkFileId}
              folderId={option.folderId}
              variantId={option.variantId}
            />
            <MarkReady
              isLoading={readyLoading || notReadyLoading}
              isReady={option.isReady}
              hasAnyBasedOn={option.hasAnyBasedOn}
              onClick={markReady}
              containerWarningRef={ref}
            />
          </Flex>
          <Flex
            gap={2}
            align="center"
            transition="opacity 200ms ease, transform 200ms ease"
            flexWrap="wrap"
            opacity={0}
            transform="translateX(0)"
            _groupHover={{ opacity: 1, transform: 'translateX(22px)' }}
            pb={1}
          >
            {option.isReady && (
              <Tooltip label={t('compositionGroup.option.markNotReady')}>
                <IconButton
                  onClick={markNotReady}
                  icon={<Icons.Undo boxSize={defaultIconSize} />}
                  variant="no-bg-dark"
                  aria-label={t('compositionGroup.option.markNotReady')}
                />
              </Tooltip>
            )}
            {!isVdc && (
              <Tooltip label={t('general.rename')}>
                <IconButton
                  onClick={() =>
                    onEditGroupOpen({ id: option.id, note: option.note })
                  }
                  icon={<Icons.NoteEdit boxSize={defaultIconSize} />}
                  variant="no-bg-dark"
                  aria-label={t('general.rename')}
                />
              </Tooltip>
            )}

            <Tooltip label={t('composition.archivedCompositions')}>
              <IconButton
                onClick={onArchivedCompositionsOpen}
                icon={<Icons.LayersOutline boxSize={defaultIconSize} />}
                variant="no-bg-dark"
                aria-label={t('composition.archivedCompositions')}
              />
            </Tooltip>
            {onCopy && !isVdc && (
              <Tooltip label={t('composition.copyIntoNewOption')}>
                <IconButton
                  onClick={() =>
                    onCopyOpen({ version: option.version, id: option.id })
                  }
                  icon={<Icons.ContentCopy boxSize={defaultIconSize} />}
                  variant="no-bg-dark"
                  aria-label={t('composition.copyIntoNewOption')}
                />
              </Tooltip>
            )}

            <Tooltip label={t('history.viewHistory')}>
              <IconButton
                onClick={() =>
                  onHistoryOpen({
                    id: option.id,
                    name: option.note
                      ? t('compositionGroup.option.optionNrNote', {
                          note: option.note,
                          nr: option.version,
                        })
                      : t('compositionGroup.option.optionNr', {
                          nr: option.version,
                        }),
                  })
                }
                variant="no-bg-dark"
                icon={<Icons.Clock boxSize={defaultIconSize} />}
                aria-label={t('history.viewHistory')}
              />
            </Tooltip>
            {onArchive && !isVdc && (
              <Tooltip label={t('compositionGroup.option.archive')}>
                <IconButton
                  onClick={onArchiveOpen}
                  icon={<Icons.ArchiveOutline boxSize={defaultIconSize} />}
                  variant="no-bg-dark"
                  color="red.200"
                  _light={{ color: 'red.400' }}
                  aria-label={t('compositionGroup.option.archive')}
                />
              </Tooltip>
            )}
          </Flex>
        </Flex>
      </Flex>
      <Divider py={2} />
      <Tabs w="full" variant="composition" pt={4}>
        <TabList gap={4}>
          <WarningComponent
            isMandatory={true}
            warning={compositionTabWarning(option, t)}
          >
            <Tab h="full">
              <Flex gap={1} align="center">
                <Icons.Layers /> <Text>{t('composition.compositions')}</Text>
              </Flex>
            </Tab>
          </WarningComponent>
          {isMatrix && option.colorAxis && (
            <>
              <WarningComponent warning={colorsWarning}>
                <Tab
                  h="full"
                  flexDir="column"
                  alignItems="start"
                  isDisabled={hasNoMaterials(option)}
                >
                  <Flex gap={1} align="center">
                    <Icons.PaletteSwatch /> <Text>{t('general.colors')}</Text>
                  </Flex>
                  {hasNoMaterials(option) && (
                    <Text
                      color="yellow.300"
                      _light={{ color: 'orange.500' }}
                      fontSize="sm"
                    >
                      {t('colors.configureMaterialsFirst')}
                    </Text>
                  )}
                </Tab>
              </WarningComponent>

              <WarningComponent warning={sizesWarning}>
                <Tab
                  h="full"
                  flexDir="column"
                  alignItems="start"
                  isDisabled={hasNoDimensionGroups(option)}
                >
                  <Flex gap={1} align="center">
                    <Icons.AxisArrow />
                    <Text>{t('configuration.dimensions')}</Text>
                  </Flex>
                  {hasNoDimensionGroups(option) && (
                    <Text
                      color="yellow.300"
                      _light={{ color: 'orange.500' }}
                      fontSize="sm"
                    >
                      {t('sizes.configureDimensionsFirst')}
                    </Text>
                  )}
                </Tab>
              </WarningComponent>
            </>
          )}
        </TabList>
        <TabPanels pt="6">
          <TabPanel p={0}>
            <OptionsComponent
              isVdc={isVdc}
              isMatrix={isMatrix}
              option={option}
            />
          </TabPanel>

          {isMatrix && option.colorAxis && (
            <TabPanel>
              <MatrixColors
                colorAxis={option.colorAxis}
                compositionGroupId={option.id}
                onLoad={(groups) => {
                  const warning = hasAnyMaterialNoColors(groups);
                  if (!warning) {
                    setColorsWarning(undefined);
                    return;
                  }
                  setColorsWarning(t('colors.someMissing'));
                }}
              />
            </TabPanel>
          )}
          {isMatrix && option.colorAxis && (
            <TabPanel>
              <MatrixSizes
                colorAxis={option.colorAxis}
                compositionGroupId={option.id}
                onAnyValueChanged={(nrOfZeroDimensions) => {
                  const warning = nrOfZeroDimensions > 0;
                  if (!warning) {
                    setSizesWarning(undefined);
                    return;
                  }
                  setSizesWarning(t('sizes.fillAll'));
                }}
              />
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>

      <DocumentFileModal
        fileId={option.fileId}
        onRemoveFileOpen={onRemoveFileOpen}
        onFileBrowserOpen={onFileBrowserOpen}
        onUpload={(files) => uploadFiles(files, option.folderId)}
        isUploading={isUploading}
        uploadFailed={uploadFailed}
        fileUploads={fileUploads}
        removeFileUpload={removeFileUpload}
        isOpen={isFileModalOpen}
        onClose={onFileModalClose}
      />
      <FileBrowserModal
        rootFolderId={option.folderId}
        mode="Select"
        isOpen={isFileBrowserOpen}
        onClose={onFileBrowserClose}
        onSelect={(file: FileLink) => {
          updateFile(file);
          onFileBrowserClose();
        }}
        rejectExtensionFilter={imageExtensions}
      />
      <VerifyDialogWithRequest
        headerTitle={t('fileBrowser.removeLayout')}
        secondaryButtonTitle={t('general.cancel')}
        primaryButtonTitle={t('general.confirm')}
        primaryRequest={compositionGroupApi.deleteFile}
        args={[option.id]}
        isOpen={isRemoveFileOpen}
        onClose={onRemoveFileClose}
        onPerformed={() => compositionGroupEvents.onLayoutChanged.dispatch()}
        onSuccessTitle={t('fileBrowser.fileRemoved')}
      >
        {t('alert.areYouSure')}
      </VerifyDialogWithRequest>
      <RenameGroupModal
        onUpdated={(note) => {
          compositionGroupEvents.onRename.dispatch();
          if (!option.linkedVariantId) return;
          reactEvents.variantNameChanged.dispatch({
            id: option.linkedVariantId,
            value: note,
          });
        }}
        group={editGroupValue}
        onClose={onEditGroupClose}
        isOpen={isEditGroupOpen}
      />
      {onArchive && (
        <VerifyDialogWithFunction
          headerTitle={t('compositionGroup.option.archive')}
          secondaryButtonTitle={t('general.cancel')}
          primaryButtonTitle={t('general.confirm')}
          toPerform={async () => {
            await onArchive();
            onArchiveClose();
          }}
          isOpen={isArchiveOpen}
          onClose={onArchiveClose}
        >
          {t('compositionGroup.option.archiveDesc')}
        </VerifyDialogWithFunction>
      )}

      <CompositionListModal
        isOpen={isArchivedCompositionsOpen}
        onClose={onArchivedCompositionsClose}
        compositionGroupId={option.id}
      />
      {copyValue && onCopy && (
        <ConfirmCopy
          title="Copy option"
          buttonLabel={t('composition.copyComposition')}
          isOpen={isCopyOpen}
          onClose={onCopyClose}
          compositionGroupId={copyValue.id}
          copyConfigOptions={{
            linkedVariantId: option.linkedVariantId,
            sourceGroupNote: option.note ? `${option.note} - Copy` : undefined,
          }}
          onConfirm={(id, copyConfig) => {
            onCopyClose();
            onCopy(id, copyConfig);
          }}
        />
      )}
      {historyValue && (
        <CompositionGroupHistoryModal
          isOpen={isHistoryOpen}
          onClose={onHistoryClose}
          compositionGroupId={historyValue.id}
          name={historyValue.name}
        />
      )}
    </Box>
  );
}

function hasNoMaterials(option: CompositionGroup) {
  return option.compositions.every((c) => c.materials.length === 0);
}

function hasNoDimensionGroups(option: CompositionGroup) {
  return option.compositions.every((c) => c.dimensionGroup === null);
}

function hasAnyMaterialNoColors(groups: CompositionGroup[]) {
  return groups.some((g) =>
    g.compositions.some((c) => c.materials.some((d) => d.colors.length === 0)),
  );
}

function compositionTabWarning(option: CompositionGroup, t: TFunction) {
  if (option.compositions.length === 0) {
    return t('compositionGroup.option.missingCompositions');
  }

  if (option.compositions.some((c) => c.materials.length === 0)) {
    return t('compositionGroup.option.missingMaterials');
  }

  if (option.compositions.some((c) => c.dimensionGroup === null)) {
    return t('compositionGroup.option.missingDimensions');
  }

  return undefined;
}
